import React, { createContext, useContext, useEffect, useReducer, useRef as _useRef, useCallback as _useCallback } from 'react';
// Import localSettings lazily
// import { localSettings } from '@utils/localSettings';

const ThemeContext = createContext();

export const usePrivateTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('usePrivateTheme must be used within a ThemeProvider');
  }
  return context;
};

// Define initial state
const initialThemeState = {
  isDarkMode: false,
  isSystemTheme: false
};

// Define theme reducer
const themeReducer = (state, action) => {
  switch (action.type) {
    case 'SET_DARK_MODE':
      return {
        ...state,
        isDarkMode: action.payload,
        isSystemTheme: false
      };
    case 'TOGGLE_DARK_MODE':
      return {
        ...state,
        isDarkMode: !state.isDarkMode,
        isSystemTheme: false
      };
    default:
      return state;
  }
};

// Helper function to get localSettings
const getLocalSettings = () => {
  const { localSettings } = require('../utils/localSettings');
  return localSettings;
};

// Initialize theme state from local storage or system preference
const initializeTheme = () => {
  const savedTheme = getLocalSettings().get('theme');
  const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  
  if (savedTheme) {
    return { isDarkMode: savedTheme === 'dark', isSystemTheme: false };
  }
  
  return { isDarkMode: systemPrefersDark, isSystemTheme: true };
};

export const PrivateThemeProvider = ({ children }) => {
  const [state, dispatch] = useReducer(themeReducer, initialThemeState, initializeTheme);
  const { isDarkMode, isSystemTheme } = state;

  // Single effect to handle all theme-related setup and changes
  useEffect(() => {
    const newTheme = isDarkMode ? 'dark' : 'light';
    
    // Only save to localStorage if not using system theme
    if (!isSystemTheme) {
      getLocalSettings().set('theme', newTheme);
    }

    // Apply theme to document
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Listen for system theme changes only if using system theme
    if (isSystemTheme) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => dispatch({ type: 'SET_DARK_MODE', payload: e.matches });
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [isDarkMode, isSystemTheme]);

  const toggleDarkMode = () => {
    dispatch({ type: 'TOGGLE_DARK_MODE' });
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
}; 